import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, X, Clock } from "lucide-react";

const events = [
  {
    name: "Hari Pertama Puasa Ramadan 1447 H",
    date: "2026-02-19T00:00:00+07:00",
    color: "#58B09C",
  },
  {
    name: "Hari Raya Idul Fitri 1447 H",
    date: "2026-03-20T00:00:00+07:00",
    color: "#FF8A65",
  },
  {
    name: "Wuquf di Arafah 1447 H",
    date: "2026-05-26T00:00:00+07:00",
    color: "#FFC4B2",
  },
];

const CountdownSection = () => {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [countdowns, setCountdowns] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Ambil waktu Jakarta dari API (dengan fallback)
  const fetchJakartaTime = async () => {
    try {
      const res = await fetch("https://worldtimeapi.org/api/timezone/Asia/Jakarta");
      if (!res.ok) throw new Error("Response bukan 200 OK");
      const data = await res.json();
      if (data?.datetime) {
        setNow(new Date(data.datetime));
      } else {
        // fallback jika API tidak mengembalikan waktu
        setNow(new Date());
      }
    } catch (err) {
      console.warn("Gagal ambil waktu Jakarta, pakai waktu lokal:", err.message);
      setNow(new Date());
    } finally {
      setLoading(false);
    }
  };

  // Ambil waktu awal & update tiap detik
  useEffect(() => {
    fetchJakartaTime();
    const syncInterval = setInterval(fetchJakartaTime, 60000); // per 1 menit sync
    const tickInterval = setInterval(() => {
      setNow((prev) => new Date(prev.getTime() + 1000));
    }, 1000);

    return () => {
      clearInterval(syncInterval);
      clearInterval(tickInterval);
    };
  }, []);

  // Hitung mundur
  useEffect(() => {
    if (!now) return;
    const updated = events.map((event) => {
      const target = new Date(event.date).getTime();
      const diff = target - now.getTime();
      if (diff <= 0)
        return { ...event, days: 0, hours: 0, minutes: 0, seconds: 0 };

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      return { ...event, days, hours, minutes, seconds };
    });
    setCountdowns(updated);
  }, [now]);

  return (
    <>
      {/* Tombol melayang */}
      <motion.button
        onClick={() => setOpen(true)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="fixed bottom-[105px] right-6 z-[60] bg-gradient-to-br from-[#FF8A65] to-[#FF6B4A] text-white w-16 h-16 rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center"
      >
        <CalendarDays className="w-8 h-8" />
      </motion.button>

      {/* Popup */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="relative bg-gradient-to-b from-[#FFF8F6] via-[#FFF5F2] to-[#FFFFFF] rounded-3xl shadow-2xl p-6 sm:p-8 w-full max-w-lg mx-auto border border-[#FFD8C8]/50"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 text-[#FF8A65] hover:text-[#FF6B4A]"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center justify-center gap-2 mb-6">
                <CalendarDays className="w-6 h-6 text-[#FF8A65]" />
                <h3 className="text-xl sm:text-2xl font-bold text-[#333]">
                  Countdown Hari Besar Islam
                </h3>
              </div>

              {loading ? (
                <p className="text-center text-[#999] text-sm">
                  Mengambil waktu Jakarta...
                </p>
              ) : (
                <div className="flex flex-col gap-4 sm:gap-5">
                  {countdowns.map((event, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="bg-white/90 rounded-2xl border border-[#FFD8C8]/50 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 sm:items-center"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                          style={{ backgroundColor: event.color }}
                        >
                          <Clock className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#333] leading-tight">
                            {event.name}
                          </p>
                          <p className="text-xs text-[#777]">
                            {new Date(event.date).toLocaleDateString("id-ID", {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                              timeZone: "Asia/Jakarta",
                            })}
                          </p>
                        </div>
                      </div>

                      <div
                        className="text-sm sm:text-base font-semibold flex justify-start sm:justify-end gap-1 sm:gap-2 text-nowrap mt-2 sm:mt-0"
                        style={{ color: event.color }}
                      >
                        <span>{event.days}h</span>:
                        <span>{String(event.hours).padStart(2, "0")}j</span>:
                        <span>{String(event.minutes).padStart(2, "0")}m</span>:
                        <span>{String(event.seconds).padStart(2, "0")}d</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CountdownSection;
